import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DashboardLayout from "../../../../Layout/DashboardLayout";
import FileUploader from "./components/FileUploader";
import { Modal, Button, Label, TextInput } from "flowbite-react";
import { errorToast, successToast } from "../../../../Components/ToastMessage";
import axios from "axios";
import AddInterviewer from "./components/AddInterviewer";
import { useNavigate } from "react-router-dom";

const AddInterview = () => {
  const [positions, setPositions] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInterviewerModalOpen, setIsInterviewerModalOpen] = useState(false);
  const [newPosition, setNewPosition] = useState("");

  const navigate = useNavigate();

  const getPositions = useCallback(async () => {
    try {
      const response = await axios.get("/positions");
      if (response?.data) {
        setPositions(response.data);
      }
    } catch (error) {
      errorToast(error.message);
    }
  }, []);

  const addPosition = useCallback(async (values) => {
    try {
      const response = await axios.post("/positions", { name: values });
      if (response?.data) {
        setPositions((prevData) => {
          if (prevData.some((data) => data._id === response.data._id)) {
            return prevData;
          }
          return [...prevData, response.data];
        });
      }
    } catch (error) {
      errorToast(error.message);
    }
  }, []);

  const getUsers = useCallback(async () => {
    try {
      const response = await axios.get("/users");
      if (response?.data) {
        setInterviewers(response.data);
      }
    } catch (error) {
      errorToast(error.message);
    }
  }, []);

  const addInterview = useCallback(
    async (values, setSubmitting, resetForm) => {
      const formData = new FormData();
      Object.keys(values).forEach((data) => {
        if (data === "candidate") {
          formData.append("name", values[data]);
        } else {
          formData.append(data, values[data]);
        }
      });
      try {
        const response = await axios.post("/interviews", formData, {
          rawRequest: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response?.data) {
          successToast("Interview Created Successfully");
          resetForm();
          navigate("/interview-upcomming");
        }
      } catch (error) {
        errorToast(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    [navigate]
  );

  // Form validation schema using Yup
  const validationSchema = Yup.object({
    candidate: Yup.string()
      .min(3, "Candidate name must be at least 3 characters")
      .required("Candidate name is required"),
    email: Yup.string()
      .email("Email must have a valid format.")
      .required("Email is required"),
    position: Yup.string().required("Position is required"),
    date: Yup.date()
      .required("Interview date is required")
      .typeError("Invalid date format"),
    time: Yup.string().required("Interview time is required"),
    interviewer: Yup.string().required("Interviewer name is required"),
    resume: Yup.mixed().required("Resume is required"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      candidate: "",
      email: "",
      position: "",
      date: "",
      time: "",
      interviewer: "",
      resume: null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      await addInterview(values, setSubmitting, resetForm);
    },
  });

  // Function to handle adding a new position
  const handleAddPosition = async () => {
    if (newPosition.trim() !== "") {
      await addPosition(newPosition);
      setNewPosition("");
      setIsModalOpen(false);
    } else {
      alert("Position cannot be empty.");
    }
  };

  useEffect(() => {
    // if (!positions.length) {
    getPositions();
    getUsers();
    // }
  }, [getPositions, getUsers]);

  return (
    <DashboardLayout>
      <div className="bg-gray-100 h-full p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Add New Interview
        </h1>

        <div className="md:mx-10 mx-5 bg-white shadow-md rounded-2xl p-6">
          <form onSubmit={formik.handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-2 items-center">
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Candidate Name
                </label>
                <input
                  type="text"
                  name="candidate"
                  value={formik.values.candidate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter candidate's name"
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                    formik.touched.candidate && formik.errors.candidate
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {formik.touched.candidate && formik.errors.candidate && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.candidate}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Candidate Email
                </label>
                <input
                  type="text"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter candidate's name"
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.email}
                  </div>
                )}
              </div>

              <div className="mb-4 relative">
                <label className="block text-gray-700 font-semibold mb-2">
                  Position
                </label>
                <div className="flex items-center">
                  <select
                    name="position"
                    value={formik.values.position}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                      formik.touched.position && formik.errors.position
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="" label="Select position" />
                    {positions.map((position) => (
                      <option key={position?._id} value={position?._id}>
                        {position?.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ml-2 text-slate-500 w-[30px] h-[30px] text-center rounded-full border-slate-500 border hover:text-white hover:bg-slate-500 transition"
                    onClick={() => setIsModalOpen(true)}
                  >
                    +
                  </button>
                </div>
                {formik.touched.position && formik.errors.position && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.position}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Interview Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                    formik.touched.date && formik.errors.date
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {formik.touched.date && formik.errors.date && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.date}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Interview Time
                </label>
                <input
                  type="time"
                  name="time"
                  value={formik.values.time}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                    formik.touched.time && formik.errors.time
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {formik.touched.time && formik.errors.time && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.time}
                  </div>
                )}
              </div>

              <div className="mb-4 relative">
                <label className="block text-gray-700 font-semibold mb-2">
                  Interviewer
                </label>
                <div className="flex items-center">
                  <select
                    name="interviewer"
                    value={formik.values.interviewer}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full border rounded-lg px-4 py-2 focus:outline-none ${
                      formik.touched.interviewer && formik.errors.interviewer
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="" label="Select interviewer" />
                    {interviewers.map((interviewer) => (
                      <option key={interviewer?._id} value={interviewer?._id}>
                        {interviewer?.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="ml-2 text-slate-500 w-[30px] h-[30px] text-center rounded-full border-slate-500 border hover:text-white hover:bg-slate-500 transition"
                    onClick={() => setIsInterviewerModalOpen(true)}
                  >
                    +
                  </button>
                </div>
                {formik.touched.position && formik.errors.position && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.position}
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <FileUploader
                value={formik.values.resume?.name}
                handleChange={(e) => {
                  formik.setFieldValue("resume", e.target.files[0]);
                }}
              />
              {formik.touched.resume && formik.errors.resume && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.resume}
                </div>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-slate-500 text-white py-2 px-4 rounded-lg hover:bg-slate-600 transition"
            >
              Add Interview
            </button>
          </form>
        </div>
      </div>

      {/* Flowbite Modal */}
      {isModalOpen ? (
        <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <Modal.Header>Add New Position</Modal.Header>
          <Modal.Body>
            <div className="space-y-4">
              <Label htmlFor="new-position" value="Position Name" />
              <TextInput
                id="new-position"
                type="text"
                placeholder="Enter new position"
                value={newPosition}
                onChange={(e) => setNewPosition(e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button color="gray" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <button
              onClick={handleAddPosition}
              className=" bg-slate-500 text-white py-2 px-4 rounded-lg hover:bg-slate-600 transition"
            >
              Add Position
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}

      {isInterviewerModalOpen ? (
        <AddInterviewer
          isInterviewerModalOpen={isInterviewerModalOpen}
          setIsInterviewerModalOpen={setIsInterviewerModalOpen}
          setInterviewers={setInterviewers}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AddInterview;
